import { useEffect, useState } from "react";
import AdminLayout, { PageHeader } from "../../components/admin/AdminLayout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import FileUploader from "../../components/admin/FileUploader";
import SeoFields from "../../components/admin/SeoFields";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { SortableProvider, SortableItems, SortableItem, DragHandle } from "../../components/admin/SortableList";

const empty = {
  title: "", category_id: "", excerpt: "", content: "", cover_url: "", author: "", status: "draft",
  slug: "",
  author_bio: "", author_credentials: "", medical_reviewer: "", last_reviewed_at: "",
  meta_title: "", meta_description: "", meta_keywords: "", og_image: "", canonical_url: "", robots: "", locale: "en-IN",
};

export default function Blogs() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/admin/blogs");
    setItems(data);
  };
  const loadCats = async () => {
    const { data } = await api.get("/admin/categories?kind=blog");
    setCats(data);
  };
  useEffect(() => { load(); loadCats(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ ...empty, ...it, category_id: it.category_id || "" });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, category_id: form.category_id || null };
      if (editing) await api.put(`/admin/blogs/${editing.id}`, payload);
      else await api.post("/admin/blogs", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete blog?")) return;
    try { await api.delete(`/admin/blogs/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout>
      <PageHeader
        title="Blogs"
        subtitle="Editorial posts, news, and articles."
        actions={
          <Button onClick={openNew} data-testid="add-blog-btn" className="bg-[#C2410C] hover:bg-[#9A3412] text-white">
            <Plus size={16} className="mr-2" />New post
          </Button>
        }
      />
      <div className="p-8">
        <SortableProvider items={items} setItems={setItems} reorderEndpoint="/admin/blogs/reorder">
          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="blogs-table">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F8F6] text-left text-[10px] tracking-[0.2em] uppercase text-[#4B5563]">
                <tr>
                  <th className="px-3 py-3 w-10"></th>
                  <th className="px-6 py-3">Title</th>
                  <th className="px-6 py-3">Category</th>
                  <th className="px-6 py-3">Author</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {items.length === 0 && <tr><td colSpan={6} className="px-6 py-10 text-center text-[#4B5563]">No posts yet.</td></tr>}
                <SortableItems items={items}>
                  {items.map((it) => (
                    <SortableItem key={it.id} id={it.id} as="tr" data-testid={`blog-row-${it.id}`}>
                      {(handleProps) => (
                        <>
                          <td className="px-3 py-3"><DragHandle handleProps={handleProps} /></td>
                          <td className="px-6 py-3 font-medium text-[#1F2937]">{it.title}</td>
                          <td className="px-6 py-3 text-[#4B5563]">{it.category_name || "—"}</td>
                          <td className="px-6 py-3 text-[#4B5563]">{it.author || "—"}</td>
                          <td className="px-6 py-3">
                            <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded ${
                              it.status === "published" ? "bg-[#C2410C]/10 text-[#C2410C]" : "bg-[#E2E8F0] text-[#4B5563]"
                            }`}>{it.status}</span>
                          </td>
                          <td className="px-6 py-3 text-right">
                            <button onClick={() => openEdit(it)} data-testid={`edit-${it.id}`} className="text-[#4B5563] hover:text-[#C2410C] mr-3"><PencilSimple size={16} /></button>
                            <button onClick={() => remove(it.id)} data-testid={`delete-${it.id}`} className="text-[#4B5563] hover:text-[#EF4444]"><Trash size={16} /></button>
                          </td>
                        </>
                      )}
                    </SortableItem>
                  ))}
                </SortableItems>
              </tbody>
            </table>
          </div>
        </SortableProvider>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="blog-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit post" : "New post"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2 space-y-2">
                <Label>Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="blog-title" />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="blog-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="blog-category"><SelectValue placeholder="Choose category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Author</Label>
                <Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="blog-author" />
              </div>
              <div className="md:col-span-3 space-y-2">
                <Label>Excerpt</Label>
                <Textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="blog-excerpt" />
              </div>
              <div className="md:col-span-3 space-y-2">
                <Label>Content (Markdown / HTML)</Label>
                <Textarea rows={10} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="blog-content" />
              </div>
              <div className="md:col-span-3">
                <FileUploader
                  kind="image" accept="image/*" label="Cover image"
                  currentUrl={form.cover_url}
                  onUploaded={(url) => setForm({ ...form, cover_url: url })}
                  testId="upload-cover"
                />
              </div>
            </div>

            {/* Medical E-E-A-T */}
            <div className="pt-4 border-t border-[#E2E8F0] space-y-4">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#4B5563] font-semibold">Medical · Trust & E-E-A-T</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Author bio</Label><Textarea rows={2} value={form.author_bio || ""} onChange={(e) => setForm({ ...form, author_bio: e.target.value })} data-testid="blog-author-bio" /></div>
                <div className="space-y-2"><Label>Author credentials</Label><Input value={form.author_credentials || ""} onChange={(e) => setForm({ ...form, author_credentials: e.target.value })} placeholder="MD, PharmD, etc." data-testid="blog-credentials" /></div>
                <div className="space-y-2"><Label>Medically reviewed by</Label><Input value={form.medical_reviewer || ""} onChange={(e) => setForm({ ...form, medical_reviewer: e.target.value })} placeholder="Dr. ___" data-testid="blog-reviewer" /></div>
                <div className="space-y-2"><Label>Last reviewed on (date)</Label><Input type="date" value={(form.last_reviewed_at || "").substring(0, 10)} onChange={(e) => setForm({ ...form, last_reviewed_at: e.target.value })} data-testid="blog-reviewed-at" /></div>
              </div>
            </div>

            <SeoFields form={form} setForm={setForm} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-blog" className="bg-[#C2410C] hover:bg-[#9A3412]">Save post</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
